import React, { useMemo } from "react";
import { useGLTF } from "@react-three/drei";
import { Box3, TextureLoader, Vector3 } from "three";
import { PrimitiveProps, useLoader } from "@react-three/fiber";
import { Landmark } from "@mediapipe/tasks-vision";
import { scaleLandmark } from "./utils";

interface NecklaceMeshProps extends Omit<PrimitiveProps, "object"> {
  model?: string;
  image?: string;
  landmarks: Landmark[];
  videoWidth: number,
  videoHeight: number,
}

export const POSE_LANDMARKS = {
  RIGHT_SHOULDER: 11,
  LEFT_SHOULDER: 12,
  NOSE: 0,
};

export const calculateMidX = (landmark1: Landmark, landmark2: Landmark) => ({
  ...landmark1,
  x: (landmark1.x + landmark2.x) / 2,
});

export const calculateMidY = (landmark1: Landmark, landmark2: Landmark) => ({
  ...landmark1,
  y: (landmark1.y + landmark2.y) / 2,
});


const smoothingBuffer = {
  positions: [] as [number, number, number][],
  scales: [] as [number, number, number][],
  maxBufferSize: 10,
};

const addToBuffer = (buffer: any[], value: any) => {
  if (buffer.length >= smoothingBuffer.maxBufferSize) {
    buffer.shift();
  }
  buffer.push(value);
};

const calculateAverage = (buffer: any[]) => {
  if (buffer.length === 0) return [0, 0, 0];
  return buffer[0].map((_: number, i: number) =>
    buffer.reduce((sum, value) => sum + value[i], 0) / buffer.length
  );
};

const getNeckInfo = ({landmarks, videoWidth, videoHeight, scaleDeviation, yDeviation}
:{  landmarks: Landmark[],
  videoWidth: number,
  videoHeight: number,
  scaleDeviation: number,
  yDeviation: number,}
) => {
  
  const leftShoulderLandmark = scaleLandmark(
    landmarks[POSE_LANDMARKS.LEFT_SHOULDER],
    videoWidth,
    videoHeight
  );
  const rightShoulderLandmark = scaleLandmark(
    landmarks[POSE_LANDMARKS.RIGHT_SHOULDER],
    videoWidth,
    videoHeight
  );

  const shoulderDist = Math.sqrt(
    (leftShoulderLandmark.x - rightShoulderLandmark.x) ** 2 +
      (leftShoulderLandmark.y - rightShoulderLandmark.y) ** 2 +
      (leftShoulderLandmark.z - rightShoulderLandmark.z) ** 2
  );
  const rawScaleValue = shoulderDist * scaleDeviation;
  const rawScale = [rawScaleValue, rawScaleValue, rawScaleValue];

  const neckLandMark = calculateMidX(
    leftShoulderLandmark,
    rightShoulderLandmark
  );
  const yOffset = rawScaleValue * yDeviation;

  const rawPosition = [neckLandMark.x, neckLandMark.y + yOffset, neckLandMark.z];

  addToBuffer(smoothingBuffer.positions, rawPosition);
  addToBuffer(smoothingBuffer.scales, rawScale);

  const smoothedPosition = calculateAverage(smoothingBuffer.positions);
  const smoothedScale = calculateAverage(smoothingBuffer.scales);

  return {
    scale: smoothedScale,
    position: smoothedPosition,
    rotation: [0, 0, 0] as any,
  };
};

const NecklaceMesh = ({ model, image, landmarks,videoWidth,
  videoHeight, ...props }: NecklaceMeshProps) => {

  return (
    <>
        {model && <Render3DModel model={model} landmarks={landmarks} videoHeight={videoHeight} videoWidth={videoWidth}  {...props}/>}
        {image && <Render2DImage image={image} landmarks={landmarks} videoHeight={videoHeight} videoWidth={videoWidth}/>}
    </>
  );
};


const Render3DModel = ({ model, landmarks, faceLandmarks,    videoWidth,
  videoHeight, ...props }: Omit<NecklaceMeshProps, 'model' | 'image'> & { model: string } ) => {
  const gltfModel = useGLTF(model);
  const scene = Array.isArray(gltfModel) ? gltfModel[0].scene : gltfModel.scene;

  const bbox = new Box3().setFromObject(scene);
  const size = bbox.getSize(new Vector3());

  const scaleDeviation = 1 / 2
  const yDeviation = 0


  const neckInfo = useMemo(
    () => getNeckInfo({landmarks, scaleDeviation, yDeviation,videoWidth,
      videoHeight}),
    [landmarks, scaleDeviation, videoHeight, videoWidth]
  );

  return (
    <>
        <primitive
          object={scene.clone()}
          scale={neckInfo.scale}
          position={neckInfo.position}
          rotation={neckInfo.rotation}
          {...props}
        />
    </>
  );
}

const Render2DImage = ({ image, landmarks,videoWidth,
  videoHeight, }: Pick<NecklaceMeshProps, 'landmarks' | 'videoWidth' |
  'videoHeight'> & { image: string }) => {
  const texture = useLoader(TextureLoader, image);

  const size = new Vector3(1, 1, 1)
  const scaleDeviation = 1 / 2.4
  const yDeviation = 1 / 15 * (size.y)

  const neckInfo = useMemo(
    () => getNeckInfo({landmarks, scaleDeviation, yDeviation, videoWidth,
      videoHeight}),
    [landmarks, scaleDeviation, videoHeight, videoWidth, yDeviation]
  );

  return (
    <>
      <mesh
        scale={[neckInfo.scale[0], neckInfo.scale[1], size.z]}
        position={neckInfo.position}
        rotation={neckInfo.rotation}
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={texture} transparent />
      </mesh>
    </>
  );
}

export default NecklaceMesh;
